<template>
  <div
    v-if="shouldRender"
    class="cms-tab-pane"
    v-show="active"
    role="tabpanel"
    :aria-hidden="!active"
    :id="`pane-${paneName}`"
    :aria-labelledby="`tab-${paneName}`"
  >
    <slot></slot>
  </div>
</template>

<script lang="ts" setup>
import { ref, inject, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { tabPaneProps } from './tab-pane'
import { tabsContextKey } from './tabs'

defineOptions({
  name: 'CmsTabPane'
})

const props = defineProps(tabPaneProps)

// 获取父级 Tabs 组件的上下文
const tabsContext = inject(tabsContextKey)

if (!tabsContext) {
  throw new Error('TabPane must be used inside Tabs component')
}

// 标签页名称
const paneName = computed(() => props.name || '')

// 是否是激活状态
const active = computed(() => {
  return paneName.value === tabsContext.currentName.value
})

// 是否已经渲染
const loaded = ref(false)

// 是否应该渲染
const shouldRender = computed(() => {
  // 如果已经加载过或者当前是激活状态，则渲染
  if (loaded.value || active.value) {
    return true
  }
  
  // 如果不是延迟渲染，则渲染
  return !props.lazy
})

// 监听激活状态，更新加载状态
watch(
  () => active.value,
  (val) => {
    if (val) {
      loaded.value = true
    }
  },
  { immediate: true }
)

// 组件挂载时，注册到父级 Tabs 组件
onMounted(() => {
  tabsContext.registerPane({
    name: paneName.value,
    label: props.label,
    disabled: props.disabled,
    closable: props.closable,
    lazy: props.lazy
  })
})

// 组件卸载时，从父级 Tabs 组件中注销
onBeforeUnmount(() => {
  tabsContext.unregisterPane(paneName.value)
})
</script>

<style>
.cms-tab-pane {
  width: 100%;
}
</style>